import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TableBody, TableCell, TableHead, TableRow, Tooltip, CircularProgress, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import EditJsonContentDialog from 'components/common/dialogBox/EditJsonContentDialog';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { CollapseCard, IconButton } from '@totalsoft/rocket-ui';
import LoadingWrapper from 'components/common/LoadingWrapper';
import { FlowChecksTable, CascoLegendContentWrapper } from '../FlowCheckStyles';

const CascoEntitiesLegendComponent = ({ insurersData, periods }) => {
  const { t } = useTranslation();

  return (
    <CollapseCard sx={{ mb: 2 }} title={t('Settings.InsuranceIntegrationChecks.Legend.Title')} toggleOnHeaderClick>
      <CascoLegendContentWrapper>
        <strong>
          {t('Settings.InsuranceIntegrationChecks.Legend.Insurers')}
          {':'}
        </strong>
        <span>{insurersData.insurers && insurersData.insurers.length > 0 && insurersData.insurers.map(x => x.name).join(', ')}</span>
        <div>
          <strong>
            {t('Settings.InsuranceIntegrationChecks.Legend.Periods')}
            {':'}
          </strong>
          {periods && (
            <span color='primary'>
              {periods.min}
              {'-'}
              {periods.max}
            </span>
          )}
        </div>
      </CascoLegendContentWrapper>
    </CollapseCard>
  );
};

CascoEntitiesLegendComponent.propTypes = {
  insurersData: PropTypes.object,
  periods: PropTypes.object
};

const InsuranceFlowCascoChecksHeader = ({
  verificationStatus,
  configurationJSON,
  insurersData,
  periods,
  openDialog,
  onOpenDialog,
  onCloseDialog,
  onCancelDialog,
  onChangeJsonValue,
  onCheckCascoQuotations,
  onReloadJson
}) => {
  const { t } = useTranslation();

  const displayCheckResultIcon = check => {
    return (
      <>
        {check.loading === true ? (
          <CircularProgress color='secondary' />
        ) : check.success === true ? (
          <Tooltip title={t('Tooltips.Success')}>
            <CheckCircleIcon color='secondary' />
          </Tooltip>
        ) : check.success === false ? (
          <Tooltip title={t('Tooltips.Error')}>
            <ErrorIcon color='error' />
          </Tooltip>
        ) : (
          <></>
        )}
      </>
    );
  };

  const displayCheckResultInfo = useCallback(
    check => {
      return check.success === true
        ? t('Settings.ConnectionChecks.ConnectionSucceeded')
        : check.success === false
        ? t('Settings.ConnectionChecks.ConnectionFailed')
        : '';
    },
    [t]
  );

  return (
    <>
      <FlowChecksTable>
        <TableHead>
          <TableRow>
            <TableCell>{t('Settings.ConnectionChecks.CheckType')}</TableCell>
            <TableCell>{t('Settings.ConnectionChecks.Status')}</TableCell>
            <TableCell>{t('Settings.ConnectionChecks.Info')}</TableCell>
            <TableCell />
            <TableCell>{t('Settings.ConnectionChecks.Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{t('Settings.InsuranceIntegrationChecks.CascoCheck.CheckQuotations')}</TableCell>
            <TableCell>
              <LoadingWrapper loading={verificationStatus.loading}>{displayCheckResultIcon(verificationStatus)}</LoadingWrapper>
            </TableCell>
            <TableCell>
              <LoadingWrapper loading={verificationStatus.loading}>{displayCheckResultInfo(verificationStatus)}</LoadingWrapper>
            </TableCell>
            <TableCell />
            <TableCell>
              <Grid item container xs={12} justifyContent='end' sx={{ gap: '.5rem' }}>
                <IconButton
                  color='primary'
                  variant='text'
                  size={'small'}
                  onClick={onOpenDialog}
                  tooltip={t('Tooltips.Edit')}
                  disabled={verificationStatus.loading}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color='primary'
                  variant='text'
                  size={'small'}
                  onClick={onCheckCascoQuotations}
                  tooltip={t('Settings.ConnectionChecks.LaunchCheck')}
                  disabled={verificationStatus.loading}
                >
                  <PlaylistAddCheckIcon />
                </IconButton>
              </Grid>
            </TableCell>
          </TableRow>
        </TableBody>
      </FlowChecksTable>
      {openDialog ? (
        <EditJsonContentDialog
          content={configurationJSON}
          open={openDialog}
          onChange={onChangeJsonValue}
          onClose={onCloseDialog}
          onCancel={onCancelDialog}
          onConfirm={onCloseDialog}
          onReloadJson={onReloadJson}
          customHeaderComponent={<CascoEntitiesLegendComponent insurersData={insurersData} periods={periods} />}
        />
      ) : (
        <></>
      )}
    </>
  );
};

InsuranceFlowCascoChecksHeader.propTypes = {
  verificationStatus: PropTypes.object,
  configurationJSON: PropTypes.object,
  insurersData: PropTypes.object,
  insurersDataLoading: PropTypes.bool,
  periods: PropTypes.object,
  openDialog: PropTypes.bool,
  onOpenDialog: PropTypes.func,
  onCloseDialog: PropTypes.func,
  onCancelDialog: PropTypes.func,
  onChangeJsonValue: PropTypes.func,
  onCheckCascoQuotations: PropTypes.func,
  onReloadJson: PropTypes.func
};
export default InsuranceFlowCascoChecksHeader;
